import { SchemaCallbackProxy } from "@colyseus/schema"
import { getStateCallbacks, Room } from "@colyseus/sdk"
import Phaser from "phaser"
import MoveToPlugin from "phaser4-rex-plugins/plugins/moveto-plugin"
import OutlinePlugin from "phaser4-rex-plugins/plugins/outlinefilter-plugin"
import { PokemonEntity } from "../../../core/pokemon-entity"
import Simulation from "../../../core/simulation"
import Count from "../../../models/colyseus-models/count"
import Player from "../../../models/colyseus-models/player"
import Status from "../../../models/colyseus-models/status"
import SimulatorState from "../../../rooms/states/simulator-state"
import { IBoardEvent, IDps, IPokemonEntity, Transfer } from "../../../types"
import type { NonFunctionPropNames } from "../../../types/HelperTypes"
import {
  SimulatorBackgroundTheme,
  SimulatorBattlePreview,
  SimulatorSelectedPokemonDetail
} from "../../../types/interfaces/simulator"
import { transformEntityCoordinates } from "../pages/utils/utils"
import { preference } from "../preferences"
import SimulatorScene from "./scenes/simulator-scene"

export default class SimulatorContainer {
  room: Room<SimulatorState>
  $: SchemaCallbackProxy<SimulatorState>
  game: Phaser.Game
  scene: SimulatorScene
  initializedDecorationPlayers = new Set<string>()
  liveDpsMeters: { blue: Map<string, IDps>; red: Map<string, IDps> } = {
    blue: new Map(),
    red: new Map()
  }
  onDpsMetersChange?: (meters: { blue: IDps[]; red: IDps[] }) => void
  private layoutRefreshFrame?: number

  constructor(
    div: HTMLDivElement,
    room: Room<SimulatorState>,
    onAlert?: (message: string) => void,
    onDpsMetersChange?: (meters: { blue: IDps[]; red: IDps[] }) => void,
    onSelectedPokemonChange?: (
      detail: SimulatorSelectedPokemonDetail | null
    ) => void
  ) {
    this.room = room
    this.$ = getStateCallbacks(room)
    this.scene = new SimulatorScene()
    this.onDpsMetersChange = onDpsMetersChange
    this.scene.setSelectedPokemonListener(onSelectedPokemonChange)

    this.game = new Phaser.Game({
      type: Number(preference("renderer") ?? Phaser.AUTO),
      width: 1950,
      height: 1000,
      parent: div,
      pixelArt: true,
      scene: [this.scene],
      scale: { mode: Phaser.Scale.FIT },
      dom: { createContainer: true },
      disableContextMenu: true,
      plugins: {
        global: [
          {
            key: "rexMoveTo",
            plugin: MoveToPlugin,
            start: true
          }
        ]
      }
    })

    window.setTimeout(() => {
      this.game.scale.refresh()
    }, 0)

    if (this.game.renderer.type === Phaser.WEBGL) {
      this.game.plugins.install("rexOutline", OutlinePlugin, true)
    }

    this.initializeStateListeners()

    if (onAlert) {
      room.onMessage(Transfer.ALERT, onAlert)
    }

    room.onMessage(Transfer.POKEMON_DAMAGE, (message) => {
      this.scene.battle?.displayDamage(message)
    })

    room.onMessage(Transfer.POKEMON_HEAL, (message) => {
      this.scene.battle?.displayHeal(message)
    })

    room.onMessage(Transfer.ABILITY, (message) => {
      this.scene.battle?.displayAbility(message)
    })

    room.onMessage(Transfer.BOARD_EVENT, (event: IBoardEvent) => {
      if (this.scene.battle?.simulation?.id === event.simulationId) {
        this.scene.battle.displayBoardEvent(event)
      }
    })

    room.onMessage(Transfer.CLEAR_BOARD_EVENT, (event: IBoardEvent) => {
      if (this.scene.battle?.simulation?.id === event.simulationId) {
        this.scene.battle.removeBoardEvent(event)
      }
    })

    room.onMessage(Transfer.CLEAR_BOARD, (event: { simulationId: string }) => {
      if (this.scene.battle?.simulation?.id === event.simulationId) {
        this.scene.battle.clearBoardEvents()
      }
    })

    room.onMessage(Transfer.SIMULATION_STOP, () => {
      this.scene.clearSimulation()
    })

    room.onMessage(
      Transfer.DISPLAY_TEXT,
      (message: { text: string; id: string; x: number; y: number }) => {
        if (this.scene.battle?.simulation?.id === message.id && message.text) {
          const [x, y] = transformEntityCoordinates(
            message.x,
            message.y,
            this.scene.battle.flip
          )
          this.scene.displayBattleText(x, y, message.text.toUpperCase(), true)
        }
      }
    )
  }

  destroy() {
    if (this.layoutRefreshFrame != null) {
      window.cancelAnimationFrame(this.layoutRefreshFrame)
    }
    this.game.destroy(true)
  }

  setPreview(preview: SimulatorBattlePreview) {
    this.scene.setPreview(preview)
  }

  setBackgroundTheme(theme: SimulatorBackgroundTheme) {
    this.scene.setBackgroundTheme(theme)
  }

  getDropTargetAtClientPoint(clientX: number, clientY: number) {
    const rect = this.game.canvas?.getBoundingClientRect()

    if (!rect || rect.width === 0 || rect.height === 0) {
      return null
    }

    const screenX = ((clientX - rect.left) / rect.width) * this.game.scale.width
    const screenY = ((clientY - rect.top) / rect.height) * this.game.scale.height
    const worldPoint = this.scene.cameras.main.getWorldPoint(screenX, screenY)

    return this.scene.getDropTargetAt(worldPoint.x, worldPoint.y)
  }

  updateDropHoverAtClientPoint(clientX: number, clientY: number) {
    const rect = this.game.canvas?.getBoundingClientRect()

    if (!rect || rect.width === 0 || rect.height === 0) {
      return
    }

    const screenX = ((clientX - rect.left) / rect.width) * this.game.scale.width
    const screenY = ((clientY - rect.top) / rect.height) * this.game.scale.height
    const worldPoint = this.scene.cameras.main.getWorldPoint(screenX, screenY)

    this.scene.setDropHoverAt(worldPoint.x, worldPoint.y)
  }

  clearDropHover() {
    this.scene.clearDropHover()
  }

  refreshLayout() {
    if (this.layoutRefreshFrame != null) {
      window.cancelAnimationFrame(this.layoutRefreshFrame)
    }

    this.scene.resetOverlayState()
    this.layoutRefreshFrame = window.requestAnimationFrame(() => {
      this.layoutRefreshFrame = window.requestAnimationFrame(() => {
        this.layoutRefreshFrame = undefined
        this.game.scale.refresh()
        this.scene.refreshVisualState()
        this.scene.refreshSelectedPokemonDetail()
      })
    })
  }

  private initializeStateListeners() {
    const $state = this.$(this.room.state)

    const applyBluePlayer = () => {
      const bluePlayer = this.room.state.players.get(
        this.room.state.bluePlayerId
      )
      if (bluePlayer) {
        this.scene.setPlayer(bluePlayer)
        this.initializePlayerDecorations(bluePlayer)
      }
    }

    $state.listen("bluePlayerId", applyBluePlayer)
    if (this.room.state.bluePlayerId) {
      applyBluePlayer()
    }

    $state.listen("simulation", (simulation) => {
      if (simulation) {
        this.resetDpsMeters()
        this.initializeSimulation(simulation)
      } else {
        this.scene.clearSimulation()
      }
    })

    if (this.room.state.simulation) {
      this.resetDpsMeters()
      this.initializeSimulation(this.room.state.simulation)
    }
  }

  private initializeSimulation(simulation: Simulation) {
    const bluePlayer = this.room.state.players.get(simulation.bluePlayerId)
    const redPlayer = this.room.state.players.get(simulation.redPlayerId)
    this.scene.setSimulationPlayers(bluePlayer, redPlayer)
    this.scene.setSimulation(simulation)
    const $simulation = this.$<Simulation>(simulation)

    if (bluePlayer) {
      this.initializePlayerDecorations(bluePlayer)
    }
    if (redPlayer) {
      this.initializePlayerDecorations(redPlayer)
    }

    for (const team of [$simulation.blueTeam, $simulation.redTeam]) {
      team.onAdd((pokemon) =>
        this.initializePokemon(
          pokemon as PokemonEntity,
          simulation,
          team === $simulation.blueTeam
            ? simulation.bluePlayerId
            : simulation.redPlayerId
        )
      )
      team.onRemove((pokemon) => {
        this.scene.battle?.removePokemon(simulation.id, pokemon)
      })
    }

    $simulation.listen("started", (value) => {
      if (value) {
        this.scene.battle?.onSimulationStart()
        this.scene.refreshSimulationDecorations()
      }
    })

    $simulation.listen("weather", () => {
      this.scene.setSimulation(simulation)
    })

    this.initializeDpsListeners(simulation)
  }

  private initializePlayerDecorations(player: Player) {
    if (this.initializedDecorationPlayers.has(player.id)) {
      return
    }

    this.initializedDecorationPlayers.add(player.id)
    const $player = this.$<Player>(player)

    $player.synergies.onChange(() => {
      this.scene.refreshSimulationDecorations()
    })

    $player.flowerPots.onAdd(() => {
      this.scene.refreshSimulationDecorations()
    }, false)

    $player.flowerPots.onChange(() => {
      this.scene.refreshSimulationDecorations()
    })
  }

  private initializeDpsListeners(simulation: Simulation) {
    const $simulation = this.$<Simulation>(simulation)

    const teams = [
      {
        teamKey: "blue" as const,
        meter: simulation.blueDpsMeter,
        $meter: $simulation.blueDpsMeter
      },
      {
        teamKey: "red" as const,
        meter: simulation.redDpsMeter,
        $meter: $simulation.redDpsMeter
      }
    ]

    teams.forEach(({ teamKey, meter, $meter }) => {
      const registerDps = (dps: IDps) => {
        if (this.liveDpsMeters[teamKey].has(dps.id)) {
          return
        }
        this.liveDpsMeters[teamKey].set(dps.id, dps)
        this.emitDpsMeters()

        const $dps = this.$(dps as any)
        const fields = [
          "id",
          "name",
          "physicalDamage",
          "specialDamage",
          "trueDamage",
          "heal",
          "shield",
          "physicalDamageReduced",
          "specialDamageReduced",
          "shieldDamageTaken"
        ] as const

        fields.forEach((field) => {
          $dps.listen(field, () => {
            this.emitDpsMeters()
          })
        })
      }

      $meter.onAdd((dps) => {
        registerDps(dps as unknown as IDps)
      })

      $meter.onRemove((dps) => {
        this.liveDpsMeters[teamKey].delete(dps.id)
        this.emitDpsMeters()
      })

      meter.forEach((dps) => registerDps(dps))
    })
  }

  private resetDpsMeters() {
    this.liveDpsMeters.blue.clear()
    this.liveDpsMeters.red.clear()
    this.emitDpsMeters()
  }

  private emitDpsMeters() {
    this.onDpsMetersChange?.({
      blue: Array.from(this.liveDpsMeters.blue.values()),
      red: Array.from(this.liveDpsMeters.red.values())
    })
  }

  private initializePokemon(
    pokemon: PokemonEntity,
    simulation: Simulation,
    playerId: string
  ) {
    this.scene.battle?.addPokemonEntitySprite(simulation.id, pokemon, playerId)

    const $pokemon = this.$<PokemonEntity>(pokemon)

    const fields = [
      "positionX",
      "positionY",
      "orientation",
      "action",
      "critChance",
      "critPower",
      "ap",
      "luck",
      "speed",
      "hp",
      "maxHP",
      "shield",
      "pp",
      "atk",
      "def",
      "speDef",
      "range",
      "targetX",
      "targetY",
      "team",
      "index",
      "name",
      "shiny",
      "skill",
      "stars",
      "types",
      "stacks",
      "stacksRequired"
    ] satisfies (NonFunctionPropNames<PokemonEntity> & keyof IPokemonEntity)[]

    fields.forEach((field) => {
      $pokemon.listen(field, (value, previousValue) => {
        this.scene.battle?.changePokemon(
          simulation.id,
          pokemon,
          field,
          value,
          previousValue
        )
        this.scene.refreshSelectedPokemonDetail()
      })
    })

    const statusFields = [
      "armorReduction",
      "burn",
      "charm",
      "confusion",
      "curse",
      "curseVulnerability",
      "curseWeakness",
      "curseTorment",
      "curseFate",
      "electricField",
      "fairyField",
      "fatigue",
      "flinch",
      "freeze",
      "grassField",
      "paralysis",
      "pokerus",
      "poisonStacks",
      "protect",
      "skydiving",
      "psychicField",
      "resurrection",
      "resurrecting",
      "runeProtect",
      "silence",
      "sleep",
      "spikeArmor",
      "wound",
      "enraged",
      "possessed",
      "locked",
      "blinded",
      "magicBounce",
      "reflect",
      "tree"
    ] satisfies NonFunctionPropNames<Status>[]

    statusFields.forEach((field) => {
      $pokemon.status.listen(field, (_, previousValue) => {
        this.scene.battle?.changeStatus(
          simulation.id,
          pokemon,
          field,
          previousValue
        )
        this.scene.refreshSelectedPokemonDetail()
      })
    })

    $pokemon.items.onChange(() => {
      this.scene.battle?.updatePokemonItems(simulation.id, pokemon)
      this.scene.refreshSelectedPokemonDetail()
    })

    const countFields = [
      "crit",
      "dodgeCount",
      "ult",
      "fieldCount",
      "fightingBlockCount",
      "fairyCritCount",
      "starDustCount",
      "spellBlockedCount",
      "manaBurnCount",
      "moneyCount",
      "amuletCoinCount",
      "bottleCapCount",
      "attackCount",
      "tripleAttackCount",
      "upgradeCount",
      "soulDewCount",
      "muscleBandCount",
      "machRibbonCount"
    ] satisfies NonFunctionPropNames<Count>[]

    countFields.forEach((field) => {
      $pokemon.count.listen(field, (value, previousValue) => {
        this.scene.battle?.changeCount(
          simulation.id,
          pokemon,
          field,
          value,
          previousValue
        )
        this.scene.refreshSelectedPokemonDetail()
      })
    })
  }
}

